"use client";

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@headlessui/react";
import { CheckCircle, FileText, Loader2, AlertCircle, X } from "lucide-react";
import contratoService from "../../services/contratoService";
import contratoInternoService from "../../services/contratoInternoService";
import guestService from "../../services/guestService";
import { useNotifications } from "../../contexts/NotificationContext";
import { useSignalR } from "../../contexts/SignalRContext";
import ModalFirmaContrato from "./ModalFirmaContrato";
import logo from "../../assets/LOGOPLANORIA1.png";
import EnvConfig from "../../utils/config";

/**
 * Componente para la firma de contrato del invitado con integración de DocuSign
 * Se muestra después del registro inicial y antes del portal de pagos
 */
export default function FirmaContrato() {
  const { invitadoId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useNotifications();
  const { connection, conectado, conectarSignalR, desconectarSignalR } = useSignalR();

  // Estados básicos
  const [invitado, setInvitado] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirmando, setIsFirmando] = useState(false);
  const [contratoFirmado, setContratoFirmado] = useState(false);
  const [error, setError] = useState(null);
  const [contratoEnPreparacion, setContratoEnPreparacion] = useState(false);

  // Estados de tipo de contrato
  const [tipoContrato, setTipoContrato] = useState(null); // "DOCUSIGN" | "INTERNO" | "SIN_FIRMA_DIGITAL"

  // Estados de DocuSign
  const [modoFirma, setModoFirma] = useState("embedded");
  const [signingUrl, setSigningUrl] = useState(null);
  const [envelopeId, setEnvelopeId] = useState(null);
  const [mostrarIframe, setMostrarIframe] = useState(false);
  const [contratoYaCargado, setContratoYaCargado] = useState(false);
  const [iframeRef, setIframeRef] = useState(null);
  const [firmaEnProceso, setFirmaEnProceso] = useState(false); // Para mostrar "Procesando..."

  // Estados de contrato INTERNO (HTML + Canvas)
  const [contratoHTML, setContratoHTML] = useState(null);
  const [modoFirmaInterno, setModoFirmaInterno] = useState(null); // "SOLO_ACEPTAR" | "FIRMA_DIGITAL"
  const [mostrarModalContrato, setMostrarModalContrato] = useState(false);
  const [firmaCanvas, setFirmaCanvas] = useState(null);
  const [esPlantillaWord, setEsPlantillaWord] = useState(false); // Para detectar plantillas Word
  const [plantillaWordInfo, setPlantillaWordInfo] = useState(null); // Info de la plantilla Word
  const [previewWordUrl, setPreviewWordUrl] = useState(null); // URL de preview del Word generado
  const [docxBase64, setDocxBase64] = useState(null); // Base64 del Word generado para mammoth

  // Estados flotante de soporte
  const [mostrarMenuSoporte, setMostrarMenuSoporte] = useState(false);

  const enviarMensajeWp = (tipo) => {
    let mensaje = "";
    if (tipo === 1) mensaje = "Hola, mi contrato no carga para firmar.";
    else if (tipo === 2) mensaje = "";
    
    const url = EnvConfig.formatWhatsappLink(mensaje);
    window.open(url, "_blank");
    setMostrarMenuSoporte(false);
  };

  // ==========================================
  // CONECTAR A SIGNALR AL INICIAR COMPONENTE
  // ==========================================
  useEffect(() => {
    if (invitadoId && !conectado) {
      console.log('🔌 [FirmaContrato] Conectando SignalR con invitadoId:', invitadoId);
      conectarSignalR(invitadoId);
    }

    // Desconectar cuando el componente se desmonte
    return () => {
      console.log('🔌 [FirmaContrato] Desconectando SignalR al desmontar');
      desconectarSignalR();
    };
  }, [invitadoId, conectado, conectarSignalR, desconectarSignalR]);

  // ==========================================
  // LISTENER DE SIGNALR - NOTIFICACIÓN EN TIEMPO REAL
  // ==========================================
  useEffect(() => {
    if (!connection || !conectado || contratoFirmado) {
      if (!connection) {
        console.log("⏳ [FirmaContrato] SignalR connection no disponible aún");
      } else if (!conectado) {
        console.log("⏳ [FirmaContrato] SignalR no conectado aún");
      }
      return;
    }

    console.log("🎧 [FirmaContrato] Registrando listener SignalR para contrato firmado");
    console.log("📡 [FirmaContrato] Estado SignalR:", { conectado, hasConnection: !!connection });

    // Handler para la notificación de contrato firmado
    const handleContratoFirmado = (data) => {
      console.log("📨 [FirmaContrato] Notificación recibida - Contrato firmado:", data);
      
      // Verificar que sea para este invitado
      if (data.invitadoId === invitadoId) {
        console.log("✅ [FirmaContrato] Contrato confirmado para este invitado");
        console.log("📊 Estado de registro:", data.estadoRegistro);
        
        // Verificar que el proceso esté completamente terminado (estado ACTIVO)
        // Si solo está CONTRATO_FIRMADO, significa que Toku aún no se completó
        if (data.estadoRegistro === "ACTIVO") {
          console.log("✅ [FirmaContrato] Proceso completo - Toku activado");
          setFirmaEnProceso(false); // Ocultar mensaje de "Procesando..."
          setContratoFirmado(true); // Mostrar mensaje de éxito
          showSuccess("¡Contrato firmado exitosamente! Redirigiendo...");
          
          // Redirigir después de un breve delay
          setTimeout(() => {
            navigate(`/PortalPagos/${invitadoId}`);
          }, 2000);
        } else {
          console.log("⏳ [FirmaContrato] Contrato firmado pero esperando activación de Toku...");
          // Continuar mostrando "Procesando..." hasta que el polling detecte estado ACTIVO
        }
      } else {
        console.log("ℹ️ [FirmaContrato] Notificación es para otro invitado, ignorando");
      }
    };

    // Registrar el listener
    connection.on("contratoFirmado", handleContratoFirmado);
    console.log("✅ [FirmaContrato] Listener 'contratoFirmado' registrado exitosamente");

    // Cleanup al desmontar
    return () => {
      console.log("🔇 [FirmaContrato] Eliminando listener SignalR de contrato firmado");
      connection.off("contratoFirmado", handleContratoFirmado);
    };
  }, [connection, conectado, contratoFirmado, invitadoId, navigate, showSuccess]);

  // Detectar si DocuSign redirigió con status=completed
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'completed') {
      console.log("✅ DocuSign returnUrl detectado - Mostrando pantalla de procesamiento");
      setMostrarIframe(false);
      setFirmaEnProceso(true); // Mostrar pantalla de "Procesando tu firma..."
      showSuccess("¡Contrato firmado! Procesando...");
      
      // Limpiar el query parameter de la URL
      window.history.replaceState({}, '', `/firma-contrato/${invitadoId}`);
      
      // NO redirigir todavía - esperar a que SignalR notifique o el polling detecte el cambio
      // La redirección se hará cuando contratoFirmado cambie a true
    }
  }, [searchParams, invitadoId, showSuccess]);

  // Cargar información del invitado al montar
  useEffect(() => {
    const cargarInvitado = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await guestService.getInvitadoById(invitadoId);

        if (response.success) {
          setInvitado(response.data);

          if (response.data.estado_registro === "ACTIVO") {
            showSuccess("Tu registro ya está completo");
            navigate(`/PortalPagos/${invitadoId}`);
            return;
          } else if (response.data.estado_registro === "CONTRATO_FIRMADO") {
            // Si el contrato ya fue firmado, verificar si es SIN_FIRMA_DIGITAL
            // En ese caso, redirigir directamente a PortalPagos
            const esSinFirmaDigital = response.data.contrato_docusign_envelope_id === "N/A - SIN_FIRMA_DIGITAL";
            
            if (esSinFirmaDigital) {
              console.log("✅ Evento SIN_FIRMA_DIGITAL - Redirigiendo a PortalPagos");
              setTipoContrato("SIN_FIRMA_DIGITAL");
              showSuccess("Registro completado. Redirigiendo a tu portal de pagos...");
              setTimeout(() => {
                navigate(`/PortalPagos/${invitadoId}`);
              }, 1500);
              return;
            } else {
              // DocuSign o firma interna ya completada - esperar a que cambie a ACTIVO
              console.log("✅ Contrato ya firmado - Mostrando pantalla de procesamiento");
              setSigningUrl(null);
              setMostrarIframe(false);
              setFirmaEnProceso(true);
              return;
            }
          }

          // Determinar tipo de contrato para invitados PENDIENTE_FIRMA_CONTRATO
          await detectarTipoContrato(response.data);
        } else {
          setError(response.error || "No se pudo cargar la información del graduado");
        }
      } catch (err) {
        console.error("Error al cargar graduado:", err);
        setError("Error al cargar la información. Por favor, intenta nuevamente.");
      } finally {
        setIsLoading(false);
      }
    };

    cargarInvitado();
  }, [invitadoId, navigate, showSuccess]);

  /**
   * Detectar tipo de contrato del evento y cargar preview si es INTERNO
   */
  const detectarTipoContrato = async (invitadoData) => {
    try {
      console.log("🔍 Detectando tipo de contrato para evento:", invitadoData.id_evento);

      // Intentar cargar preview de contrato INTERNO
      try {
        const previewResponse = await contratoInternoService.obtenerPreview(invitadoData.id);
        
        if (previewResponse.success) {
          console.log("✅ Contrato INTERNO detectado");
          setTipoContrato("INTERNO");
          
          // Verificar si es plantilla Word
          if (previewResponse.data.tipo_plantilla === 'word') {
            console.log("📄 Plantilla Word detectada - Preview generado");
            console.log("📊 Estados establecidos:", {
              tipoContrato: "INTERNO",
              esPlantillaWord: true,
              modoFirmaInterno: previewResponse.data.modo_firma,
              plantillaWordInfo: previewResponse.data.plantilla_info,
              previewUrl: previewResponse.data.preview_url
            });
            setEsPlantillaWord(true);
            setPlantillaWordInfo(previewResponse.data.plantilla_info);
            setModoFirmaInterno(previewResponse.data.modo_firma);
            setPreviewWordUrl(previewResponse.data.preview_url); // Guardar URL de preview
            setDocxBase64(previewResponse.data.docxBase64); // Guardar base64
            setContratoHTML(null); // No hay HTML para Word
          } else {
            // Plantilla HTML tradicional
            console.log("📄 Plantilla HTML detectada - Cargando preview");
            setEsPlantillaWord(false);
            setContratoHTML(previewResponse.data.html_procesado);
            setModoFirmaInterno(previewResponse.data.modo_firma);
          }
          return;
        }
      } catch (previewError) {
        // Si falla, probablemente no es INTERNO
        console.log("ℹ️ No es contrato INTERNO, probando DocuSign...");
      }

      // Si no es INTERNO, asumir que es DocuSign
      console.log("✅ Contrato DOCUSIGN detectado");
      setTipoContrato("DOCUSIGN");
    } catch (error) {
      console.error("Error al detectar tipo de contrato:", error);
      setTipoContrato("DOCUSIGN"); // Fallback a DocuSign
    }
  };

  /**
   * Aceptar contrato INTERNO (sin firma digital)
   */
  const handleAceptarContratoInterno = async () => {
    try {
      setIsFirmando(true);
      
      // Obtener evidencia técnica
      const ipAddress = await contratoInternoService.obtenerIP();
      const userAgent = navigator.userAgent;
      const geolocalizacion = await contratoInternoService.obtenerGeolocalizacion();

      console.log("📝 Aceptando contrato INTERNO...");
      const response = await contratoInternoService.aceptarOFirmar({
        invitadoId,
        ipAddress,
        userAgent,
        geolocalizacion,
      });

      if (response.success) {
        console.log("✅ Contrato aceptado exitosamente", response.data);
        setMostrarModalContrato(false);
        
        // Si hay URL de contrato Word, mostrarlo
        if (response.data.tipo_documento === "docx" && response.data.documento_url) {
          console.log("📥 Contrato Word disponible para descarga:", response.data.documento_url);
          // Descargar automáticamente
          window.open(response.data.documento_url, "_blank");
        }
        
        showSuccess("¡Contrato aceptado! Procesando tu cuenta...");
        setFirmaEnProceso(true);
        
        // Redirigir al portal de pagos después de 3 segundos
        setTimeout(() => {
          navigate(`/PortalPagos/${invitadoId}`);
        }, 3000);
      } else {
        showError(response.error || "Error al aceptar el contrato");
      }
    } catch (error) {
      console.error("Error al aceptar contrato:", error);
      showError("Error al procesar el contrato. Intenta nuevamente.");
    } finally {
      setIsFirmando(false);
    }
  };

  /**
   * Firmar contrato INTERNO (con firma digital en canvas)
   */
  const handleFirmarContratoInterno = async (firmaBase64) => {
    try {
      setIsFirmando(true);
      
      // Obtener evidencia técnica
      const ipAddress = await contratoInternoService.obtenerIP();
      const userAgent = navigator.userAgent;
      const geolocalizacion = await contratoInternoService.obtenerGeolocalizacion();

      console.log("📝 Firmando contrato INTERNO...");
      const response = await contratoInternoService.aceptarOFirmar({
        invitadoId,
        firmaBase64,
        ipAddress,
        userAgent,
        geolocalizacion,
      });

      if (response.success) {
        console.log("✅ Contrato firmado exitosamente", response.data);
        setMostrarModalContrato(false);
        
        // Si hay URL de contrato Word, mostrarlo
        if (response.data.tipo_documento === "docx" && response.data.documento_url) {
          console.log("📥 Contrato Word disponible para descarga:", response.data.documento_url);
          // Descargar automáticamente
          window.open(response.data.documento_url, "_blank");
        }
        
        showSuccess("¡Contrato firmado! Procesando tu cuenta...");
        setFirmaEnProceso(true);
        
        // Redirigir al portal de pagos después de 3 segundos
        setTimeout(() => {
          navigate(`/PortalPagos/${invitadoId}`);
        }, 3000);
      } else {
        showError(response.error || "Error al firmar el contrato");
      }
    } catch (error) {
      console.error("Error al firmar contrato:", error);
      showError("Error al procesar el contrato. Intenta nuevamente.");
    } finally {
      setIsFirmando(false);
    }
  };

  /**
   * Envía el contrato a DocuSign
   */
  const handleFirmarContrato = useCallback(async () => {
    console.log("📝 handleFirmarContrato llamado", {
      invitadoId,
      modoFirma,
      isFirmando,
      contratoYaCargado
    });

    try {
      setIsFirmando(true);
      setError(null);

      console.log("📤 Enviando contrato a DocuSign...");
      const result = await contratoService.enviarContrato(invitadoId, modoFirma);
      console.log("📥 Respuesta del servicio:", result);

      if (result.success) {
        setEnvelopeId(result.data.envelopeId);

        if (modoFirma === "embedded") {
          console.log("✅ Modo embedded - Mostrando iframe con URL:", result.data.signingUrl);
          setSigningUrl(result.data.signingUrl);
          setMostrarIframe(true);
          showSuccess("Contrato listo para firma");
        } else {
          console.log("✅ Modo email - Contrato enviado");
          showSuccess("Contrato enviado a tu correo electrónico");
          setError("Revisa tu correo para firmar el contrato");
        }
      } else {
        console.error("❌ Error del servicio:", result.error);
        console.log("🔍 Tipo de error:", typeof result.error, "Contenido:", result.error);
        
        // Detectar si el contrato está en preparación
        if (result.error && result.error.includes("CONTRATO_EN_PREPARACION")) {
          console.log("⏳ Contrato en preparación detectado");
          setContratoEnPreparacion(true);
          setError(null); // Limpiar error genérico
        } else {
          setError(result.error || "Error al enviar el contrato");
          showError(result.error || "Error al enviar el contrato");
        }
      }
    } catch (err) {
      console.error("❌ Excepción al enviar contrato:", err);
      setError("Error al procesar el contrato. Por favor, intenta nuevamente.");
      showError("Error al procesar el contrato");
    } finally {
      setIsFirmando(false);
    }
  }, [invitadoId, modoFirma, showSuccess, showError]);

  /**
   * Maneja eventos de DocuSign (cuando se completa la firma)
   */
  const handleDocuSignEvent = (event) => {
    // Origenes permitidos de DocuSign
    const allowedOrigins = [
      "https://demo.docusign.net",
      "https://na3.docusign.net",
      "https://app.docusign.com",
      "https://apps-d.docusign.com"
    ];
    
    if (!allowedOrigins.some(origin => event.origin === origin)) {
      console.log("❌ Evento de origen no permitido:", event.origin);
      return;
    }

    console.log("📨 Evento recibido de DocuSign:", event.data);

    try {
      let data;
      
      // Intentar parsear el data (puede venir como string o objeto)
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else {
        data = event.data;
      }
      
      console.log("📋 Tipo de evento:", data.event);
      
      // Detectar eventos de finalización - DocuSign puede enviar diferentes nombres
      if (data.event === "signing_complete" || data.event === "ttl_expired") {
        console.log("✅ Firma completada en DocuSign (postMessage):", data.event);
        // Ocultar iframe INMEDIATAMENTE para evitar mostrar error de redirect
        setMostrarIframe(false);
        setFirmaEnProceso(true); // Mostrar mensaje de "Procesando..."
        showSuccess("¡Contrato firmado! Procesando...");
        
        // El estado contratoFirmado se actualizará cuando llegue la notificación de SignalR
        // o cuando el polling detecte el cambio de estado en el backend
        // Esto garantiza que el proceso de Toku se complete antes de continuar
      } else if (data.event === "cancel") {
        console.log("⚠️ Usuario canceló la firma");
        setMostrarIframe(false);
        setError("Firma cancelada. Puedes intentar nuevamente.");
        setContratoYaCargado(false); // Permitir reintento
        showError("Firma cancelada");
      } else if (data.event === "decline") {
        console.log("⚠️ Usuario rechazó la firma");
        setMostrarIframe(false);
        setError("Firma rechazada. Puedes intentar nuevamente.");
        setContratoYaCargado(false); // Permitir reintento
        showError("Firma rechazada");
      } else {
        console.log("ℹ️ Otro evento de DocuSign:", data.event);
      }
    } catch (err) {
      console.error("❌ Error al procesar evento de DocuSign:", err);
    }
  };

  // Handler para detectar cuando el iframe intenta navegar (esto causaría el error)
  const handleIframeError = () => {
    console.log("⚠️ Error en iframe detectado - ocultando iframe");
    if (mostrarIframe) {
      setMostrarIframe(false);
      setFirmaEnProceso(true);
      showSuccess("¡Contrato firmado! Procesando...");
    }
  };

  // Escuchar eventos de DocuSign
  useEffect(() => {
    if (mostrarIframe) {
      console.log("🎧 Iniciando escucha de eventos de DocuSign");
      window.addEventListener("message", handleDocuSignEvent);
      
      // Monitor agresivo: verificar el iframe cada 500ms para detectar navegación
      const monitorInterval = setInterval(() => {
        if (iframeRef && mostrarIframe) {
          try {
            // Intentar acceder al iframe - si falla con cross-origin después de estar accesible,
            // significa que está navegando
            const iframeDoc = iframeRef.contentDocument || iframeRef.contentWindow.document;
            
            // Si podemos acceder y la URL ha cambiado al returnUrl, ocultar
            if (iframeDoc && iframeDoc.location) {
              const url = iframeDoc.location.href;
              if (url.includes('status=completed') || url.includes('firma-contrato')) {
                console.log("🔍 Detectada navegación en iframe a URL de retorno");
                setMostrarIframe(false);
                setFirmaEnProceso(true);
                showSuccess("¡Contrato firmado! Procesando...");
                clearInterval(monitorInterval);
              }
            }
          } catch (err) {
            // Error de cross-origin es esperado mientras está en DocuSign
            // Silenciosamente ignoramos
          }
        }
      }, 500);
      
      // Timeout de seguridad: si después de 10 minutos no hay respuesta, mostrar error
      const timeout = setTimeout(() => {
        if (mostrarIframe && !contratoFirmado) {
          console.log("⏱️ Timeout de firma alcanzado");
          setError("La sesión de firma ha expirado. Por favor, intenta nuevamente.");
          setMostrarIframe(false);
          setContratoYaCargado(false);
        }
      }, 10 * 60 * 1000); // 10 minutos
      
      return () => {
        console.log("🔇 Deteniendo escucha de eventos de DocuSign");
        window.removeEventListener("message", handleDocuSignEvent);
        clearInterval(monitorInterval);
        clearTimeout(timeout);
      };
    }
  }, [mostrarIframe, contratoFirmado, iframeRef]);

  // Polling del estado del invitado mientras se muestra el iframe O mientras está procesando
  // SOLO COMO FALLBACK - SignalR es el mecanismo principal
  useEffect(() => {
    if ((!mostrarIframe && !firmaEnProceso) || contratoFirmado) {
      return;
    }

    console.log("🔄 [FALLBACK] Iniciando polling reducido (SignalR es el mecanismo principal)");
    
    const verificarEstado = async () => {
      try {
        const response = await guestService.getInvitadoById(invitadoId);
        
        if (response.success && response.data) {
          const estadoActual = response.data.estado_registro;
          
          // IMPORTANTE: Solo redirigir cuando el estado sea ACTIVO
          // CONTRATO_FIRMADO significa que DocuSign notificó pero Toku aún no se ha completado
          if (estadoActual === "ACTIVO") {
            console.log("✅ [FALLBACK] Proceso completado detectado por polling - Estado:", estadoActual);
            setFirmaEnProceso(false);
            setContratoFirmado(true);
            setMostrarIframe(false);
            showSuccess("¡Contrato firmado exitosamente! Redirigiendo...");
            
            // Redirigir después de un breve delay
            setTimeout(() => {
              navigate(`/PortalPagos/${invitadoId}`);
            }, 2000);
          } else if (estadoActual === "CONTRATO_FIRMADO") {
            console.log("⏳ [FALLBACK] Contrato firmado, esperando activación de Toku...");
          }
        }
      } catch (error) {
        console.error("❌ Error al verificar estado:", error);
      }
    };

    // Verificar cada 5 segundos cuando está en proceso
    // const intervalId = setInterval(verificarEstado, 5000);
    
    // Limpieza al desmontar o cuando cambie el estado
    return () => {
      console.log("🛑 Deteniendo polling de fallback");
      // clearInterval(intervalId);
    };
  }, [mostrarIframe, firmaEnProceso, contratoFirmado, invitadoId, navigate, showSuccess]);

  // Detectar errores del iframe (indicaría que se completó la firma)
  useEffect(() => {
    if (!iframeRef || !mostrarIframe) {
      return;
    }

    const handleIframeError = () => {
      console.log("⚠️ Error detectado en iframe - Probablemente se completó la firma");
      console.log("🔄 Aumentando frecuencia de polling para detectar cambio de estado...");
      // El polling ya detectará el cambio de estado
    };

    iframeRef.addEventListener('error', handleIframeError);

    return () => {
      if (iframeRef) {
        iframeRef.removeEventListener('error', handleIframeError);
      }
    };
  }, [iframeRef, mostrarIframe]);

  // Cargar automáticamente el contrato en modo embedded
  useEffect(() => {
    console.log("🔍 useEffect auto-carga evaluando condiciones:", {
      invitado: invitado?.id,
      estado_registro: invitado?.estado_registro,
      modoFirma,
      contratoYaCargado,
      mostrarIframe,
      signingUrl: signingUrl ? "presente" : "null",
      isFirmando
    });

    const cargarContratoAutomatico = async () => {
      if (
        invitado &&
        invitado.estado_registro === "PENDIENTE_FIRMA_CONTRATO" &&
        modoFirma === "embedded" &&
        !contratoYaCargado &&
        !mostrarIframe &&
        !signingUrl &&
        !isFirmando
      ) {
        console.log("✅ Todas las condiciones cumplidas - Iniciando carga automática");
        setContratoYaCargado(true);
        await handleFirmarContrato();
      } else {
        console.log("⏭️ Condiciones no cumplidas - No se carga automáticamente");
      }
    };

    cargarContratoAutomatico();
  }, [invitado, modoFirma, contratoYaCargado, mostrarIframe, signingUrl, isFirmando, handleFirmarContrato]);

  // Estado de carga
  if (isLoading) {
    return (
      <div className="bg-porcelain min-h-screen flex flex-col items-center justify-center p-6">
        <img src={logo} alt="Logo" className="w-36 h-auto mb-8" />
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-casal mx-auto mb-4" />
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error && !invitado) {
    return (
      <div className="bg-porcelain min-h-screen flex flex-col items-center justify-center p-6">
        <img src={logo} alt="Logo" className="w-36 h-auto mb-8" />
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-casal text-white px-6 py-2 rounded-lg hover:bg-casal/80 font-semibold"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  // Contrato firmado exitosamente
  if (contratoFirmado) {
    return (
      <div className="bg-porcelain min-h-screen flex flex-col items-center justify-center p-6">
        <img src={logo} alt="Logo" className="w-36 h-auto mb-8" />
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            ¡Contrato Firmado!
          </h2>
          <p className="text-gray-600 mb-6">
            Tu contrato ha sido firmado exitosamente. Estamos preparando tu
            portal de pagos...
          </p>
          <Loader2 className="w-8 h-8 animate-spin text-casal mx-auto" />
        </div>
      </div>
    );
  }

  // Firma en proceso (después de hacer clic en "Aceptar y Finalizar")
  if (firmaEnProceso) {
    return (
      <div className="bg-porcelain min-h-screen flex flex-col items-center justify-center p-6">
        <img src={logo} alt="Logo" className="w-36 h-auto mb-8" />
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="relative mb-6">
            <CheckCircle className="w-16 h-16 text-blue-500 mx-auto" />
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Procesando tu firma...
          </h2>
          <p className="text-gray-600 mb-4">
            Tu firma fue recibida exitosamente. Estamos:
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span>Validando tu contrato firmado</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span>Configurando tu cuenta de pagos</span>
              </li>
              <li className="flex items-start">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                <span>Preparando tus facturas del evento</span>
              </li>
            </ul>
          </div>
          <p className="text-xs text-gray-500">
            Esto puede tomar unos segundos. No cierres esta ventana.
          </p>
        </div>
      </div>
    );
  }

  // Contrato en preparación (cuando el admin aún no configura el contrato)
  if (contratoEnPreparacion) {
    return (
      <div className="bg-porcelain min-h-screen flex flex-col items-center justify-center p-6">
        <img src={logo} alt="Logo" className="w-36 h-auto mb-8" />
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="relative mb-6">
            <FileText className="w-16 h-16 text-blue-500 mx-auto" />
            <div className="absolute top-0 right-1/2 transform translate-x-12">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Contrato en Preparación
          </h2>
          <p className="text-gray-600 mb-6">
            El organizador del evento está configurando tu contrato.
            Por favor, vuelve a intentar en unos momentos.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800 text-left">
              <strong>¿Qué significa esto?</strong><br />
              El administrador del evento necesita configurar algunos detalles del contrato antes de que puedas firmarlo.
              Esta configuración se realiza por única vez y no tomará mucho tiempo.
            </p>
          </div>
          <Button
            onClick={() => {
              setContratoEnPreparacion(false);
              setContratoYaCargado(false);
              setError(null);
            }}
            className="w-full bg-casal text-white px-6 py-3 rounded-lg hover:bg-casal/80 font-semibold mb-3"
          >
            <FileText className="w-5 h-5 mr-2 inline" />
            Reintentar
          </Button>
          <p className="text-xs text-gray-500">
            Si el problema persiste, contacta al organizador del evento
          </p>
        </div>
      </div>
    );
  }

  // Interfaz principal
  return (
    <div className="bg-porcelain min-h-screen flex flex-col items-center justify-center p-6">
      <img src={logo} alt="Logo" className="w-36 h-auto mb-8" />

      <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <FileText className="w-16 h-16 text-casal mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Firma de Contrato
          </h1>
          <p className="text-gray-600">
            Bienvenido/a, <span className="font-semibold">{invitado?.nombre_completo}</span>
          </p>
        </div>

        {/* Información del registro */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            Información del Registro
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Teléfono:</span>
              <span className="font-medium text-gray-900">{invitado?.numero}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Cantidad de boletos:</span>
              <span className="font-medium text-gray-900">
                {invitado?.cantidad_boletos}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Estado:</span>
              <span className="inline-flex items-center rounded px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800">
                Pendiente de Firma
              </span>
            </div>
          </div>
        </div>

        {/* Instrucciones */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            Siguiente Paso
          </h4>
          <p className="text-blue-800 text-sm">
            Para completar tu registro, es necesario que firmes el contrato del
            evento. Una vez firmado, tendrás acceso a tu portal de pagos donde
            podrás realizar tus cuotas programadas.
          </p>
        </div>

        {/* Mensaje instructivo sobre el botón Finalizar (solo para DocuSign) */}
        {tipoContrato === "DOCUSIGN" && (
          <div className="mb-6 bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-amber-800">
                  <strong>Importante:</strong> Al hacer clic en el botón <strong>"Finalizar"</strong> dentro del documento, 
                  estás aceptando los términos y condiciones del contrato del evento. Asegúrate de haber leído 
                  todo el documento antes de continuar.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Visualización del contrato - DocuSign Iframe */}
        {mostrarIframe && signingUrl ? (
          <div className="mb-6">
            <div className="border-2 border-casal rounded-lg overflow-hidden relative">
              <iframe
                ref={setIframeRef}
                src={signingUrl}
                className="w-full h-[600px]"
                title="Firma de Contrato DocuSign"
                frameBorder="0"
                allow="camera; microphone"
                onError={handleIframeError}
              />
            </div>
            
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-center gap-2 mb-2">
                {conectado ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-green-700 font-semibold">Conectado en tiempo real</span>
                  </>
                ) : (
                  <>
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">Modo estándar</span>
                  </>
                )}
              </div>
              <p className="text-xs text-blue-800 text-center">
                💡 <strong>Nota:</strong> Después de firmar, {conectado ? 'serás notificado instantáneamente. Si la página no se actualiza, espera un momento.' : 'la página se actualizará automáticamente, si la página no se actualiza en 1-2 minutos, intenta recargar.'} 
                {!conectado && ' Si ves un mensaje de error, no te preocupes - tu firma está siendo procesada. Espera un momento, si la pagina no se actualiza en 1-2 minutos, intenta recargar.'} 
              </p>
            </div>
          </div>
        ) : isFirmando ? (
          <div className="border-2 border-casal rounded-lg p-8 mb-6 text-center bg-blue-50">
            <Loader2 className="w-12 h-12 text-casal mx-auto mb-3 animate-spin" />
            <p className="text-gray-700 mb-2 font-medium">
              Preparando tu contrato...
            </p>
            <p className="text-sm text-gray-500">
              Por favor espera un momento
            </p>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-6 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-2 font-medium">
              Documento del Contrato
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {tipoContrato === "INTERNO" && esPlantillaWord
                ? "Este evento usa contratos en formato Word."
                : modoFirma === "embedded" 
                  ? "El contrato se cargará automáticamente en un momento..."
                  : "Haz clic en 'Enviar Contrato' para recibirlo por correo"}
            </p>
            {tipoContrato === "INTERNO" && esPlantillaWord && plantillaWordInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="text-sm text-blue-900 mb-2">
                  <strong>📄</strong> {plantillaWordInfo.nombre || "Contrato del Evento"}
                </p>
                <p className="text-sm text-blue-700 mb-3">
                  Al firmar, se generará tu contrato personalizado en formato Word que podrás descargar.
                </p>
                
                {/* Botón de firma dentro del cuadro informativo */}
                <Button
                  onClick={() => setMostrarModalContrato(true)}
                  disabled={isFirmando}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center transition-all shadow-lg"
                >
                  {isFirmando ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      {modoFirmaInterno === "FIRMA_DIGITAL" ? "📝 Firmar Contrato Ahora" : "✅ Ver y Aceptar Contrato"}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Mensaje de error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </p>
          </div>
        )}



        {/* Selector de modo y botón (solo si no se muestra iframe) */}
        {!mostrarIframe && (
          <div className="flex flex-col space-y-3">
            {console.log("🔍 Renderizando área de botones:", {
              mostrarIframe,
              tipoContrato,
              esPlantillaWord,
              modoFirmaInterno
            })}
            
            {/* DOCUSIGN: Selector de modo de firma */}
            {tipoContrato === "DOCUSIGN" && (
              <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Método de firma:
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="modoFirma"
                        value="embedded"
                        checked={modoFirma === "embedded"}
                        onChange={(e) => setModoFirma(e.target.value)}
                        className="mr-2"
                        disabled={isFirmando}
                      />
                      <span className="text-sm text-gray-700">
                        Firmar aquí (Recomendado)
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="modoFirma"
                        value="email"
                        checked={modoFirma === "email"}
                        onChange={(e) => setModoFirma(e.target.value)}
                        className="mr-2"
                        disabled={isFirmando}
                      />
                      <span className="text-sm text-gray-700">
                        Recibir por correo
                      </span>
                    </label>
                  </div>
                </div>

                {/* Botón DocuSign */}
                {(modoFirma === "email" || (error && modoFirma === "embedded")) && (
                  <Button
                    onClick={handleFirmarContrato}
                    disabled={isFirmando}
                    className="w-full bg-casal text-white px-6 py-3 rounded-lg hover:bg-casal/80 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center"
                  >
                    {isFirmando ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {modoFirma === "email" ? "Enviando contrato..." : "Preparando contrato..."}
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        {modoFirma === "email" ? "Enviar Contrato por Email" : "Cargar Contrato"}
                      </>
                    )}
                  </Button>
                )}
              </>
            )}

            {/* INTERNO: Botón para abrir modal de firma */}
            {tipoContrato === "INTERNO" && (
              <>
                {/* Debug log */}
                {console.log("🔵 Renderizando botón INTERNO:", {
                  tipoContrato,
                  esPlantillaWord,
                  modoFirmaInterno,
                  contratoHTML: !!contratoHTML,
                  isFirmando,
                  buttonEnabled: !isFirmando && (!!contratoHTML || esPlantillaWord)
                })}
                
                {/* Mensaje guía para plantillas Word */}
                {esPlantillaWord && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4 mb-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                      <p className="text-sm font-bold text-blue-900">
                        👉 Siguiente paso: Firma tu contrato
                      </p>
                    </div>
                    <p className="text-xs text-blue-800">
                      Tu contrato se generará automáticamente cuando hagas clic en el botón de abajo.
                      {modoFirmaInterno === "FIRMA_DIGITAL" 
                        ? " Necesitarás firmar digitalmente con tu dedo o mouse."
                        : " Solo necesitas aceptar los términos."}
                    </p>
                  </div>
                )}
                
                <Button
                  onClick={() => setMostrarModalContrato(true)}
                  disabled={isFirmando || (!contratoHTML && !esPlantillaWord)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center transition-all shadow-lg"
                >
                  {isFirmando ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 mr-2" />
                      {modoFirmaInterno === "FIRMA_DIGITAL" ? "Firmar Contrato" : "Ver y Aceptar Contrato"}
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Mensaje de ayuda si no se renderiza ningún tipo */}
            {!tipoContrato && !isFirmando && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  ⏳ Detectando tipo de contrato... Si este mensaje persiste, contacta al organizador.
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500 text-center">
              Al firmar, aceptas los términos y condiciones del evento
            </p>
          </div>
        )}
      </div>

      {/* Modal de Firma INTERNO */}
      {tipoContrato === "INTERNO" && (contratoHTML || esPlantillaWord) && (
        <ModalFirmaContrato
          isOpen={mostrarModalContrato}
          onClose={() => setMostrarModalContrato(false)}
          contratoHTML={contratoHTML}
          contratoCSS=""
          modoFirma={modoFirmaInterno}
          plantillaNombre={esPlantillaWord ? (plantillaWordInfo?.nombre || "Contrato Word") : "Contrato de Participación"}
          onAceptar={handleAceptarContratoInterno}
          onFirmar={handleFirmarContratoInterno}
          isSubmitting={isFirmando}
          esPlantillaWord={esPlantillaWord}
          plantillaWordInfo={plantillaWordInfo}
          previewWordUrl={previewWordUrl}
          docxBase64={docxBase64}
        />
      )}

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500">
        <p>¿Necesitas ayuda? Contacta al organizador del evento</p>
      </div>

      {/* Botón flotante de soporte */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
        {mostrarMenuSoporte && (
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 mb-4 w-72 flex flex-col gap-2 transition-all transform origin-bottom-right">
            <h3 className="text-casal font-semibold text-sm mb-1 px-2">¿En qué podemos ayudarte?</h3>
            <button
              onClick={() => enviarMensajeWp(1)}
              className="text-left bg-gray-50 hover:bg-gray-100 text-gray-800 text-sm px-3 py-2 rounded-lg transition"
            >
              No se me carga el contrato para firmar
            </button>
            <button
              onClick={() => enviarMensajeWp(2)}
              className="text-left bg-gray-50 hover:bg-gray-100 text-gray-800 text-sm px-3 py-2 rounded-lg transition"
            >
              Otro detalle o duda
            </button>
          </div>
        )}
        <button
          onClick={() => setMostrarMenuSoporte(!mostrarMenuSoporte)}
          className="bg-[#25D366] hover:bg-[#128C7E] text-white p-3.5 rounded-full shadow-lg transition transform hover:scale-105 flex items-center justify-center"
          aria-label="Soporte por WhatsApp"
        >
          {mostrarMenuSoporte ? (
            <X className="w-7 h-7" />
          ) : (
            <svg 
              className="w-7 h-7"
              viewBox="0 0 24 24" 
              fill="currentColor" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          )}
        </button>
      </div>

    </div>
  );
}
